// Render a `lifecycle_decision: block` event with structured hints.
// Phase 10a.
//
// The plain-text `reason` always shows. When `hints` are present (DoR/DoD
// gate emits structured payload), we render the missing-fields list and the
// optional rubric verdicts. Hints with an unknown `kind` are surfaced as
// a generic key/value list — the protocol is intentionally open so later
// gates can add their own kinds without an apps/web change.

import type { GateBlockHints, RubricVerdictRef } from "@loom/shared";

import { useT } from "../../i18n/index.js";

interface BlockNoticeProps {
  readonly handler: string;
  readonly reason: string;
  readonly hints?: GateBlockHints;
}

const VERDICT_TONE: Readonly<Record<RubricVerdictRef["level"], string>> = {
  ok: "text-success-fg",
  warn: "text-caution-fg",
  fail: "text-danger-fg",
};

export function BlockNotice({ handler, hints, reason }: BlockNoticeProps) {
  const t = useT();
  return (
    <div className="rounded-md border border-danger-line bg-danger-soft p-3 text-xs text-danger-fg-3 space-y-2">
      <div>
        <span className="font-mono">{handler}</span> {t("chatRender.blocked", { reason })}
      </div>

      {hints?.missing && hints.missing.length > 0 && (
        <div>
          <div className="font-medium">{t("chatRender.missing")}</div>
          <ul className="ml-4 list-disc">
            {hints.missing.map((field, idx) => (
              <li key={`${field}-${idx}`}>{field}</li>
            ))}
          </ul>
        </div>
      )}

      {hints?.verdicts && hints.verdicts.length > 0 && (
        <div>
          <div className="font-medium">{t("chatRender.qualityHints")}</div>
          <ul className="ml-4 list-disc space-y-0.5">
            {hints.verdicts.map((v, idx) => (
              <li key={`${v.criterion}-${idx}`}>
                <span className={`font-mono ${VERDICT_TONE[v.level]}`}>
                  {v.level}
                </span>{" "}
                <span className="font-mono">{v.criterion}</span>
                {v.suggestion ? ` — ${v.suggestion}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
